import { computed, inject, Injectable, resource } from '@angular/core';
import { LastUsedRepository } from './last-used.repository';
import { AuthenticationContextService } from '@softline/auth';
import { DateService, Id, isDefined } from '@softline/core';
import { LastUsed, LastUsedList } from './last-used';

@Injectable({ providedIn: 'root' })
export class LastUsedService {
  private readonly authService = inject(AuthenticationContextService);
  private readonly repository = inject(LastUsedRepository);
  private readonly dateService = inject(DateService);

  private readonly _resource = resource({
    defaultValue: {},
    params: () => ({
      userId: this.authService.userId()
    }),
    loader: async (param) => {
      if(!isDefined(param.params.userId))
        return {};
      return await this.repository.load({ userId: param.params.userId });
    }
  })

  lastUsed = <T>(key: string) =>
    computed(() => {
      const value = this._resource.value()[key];
      return (value?.value ?? []) as LastUsed<T>[];
    });

  async add<T>(key: string, id: Id, object: T): Promise<LastUsed<T>> {
    const userId = this.authService.userId();
    if (!userId) throw new Error('[LastUsedService] add: userId is not defined');

    const hasValue = !!this._resource.value()[key];

    const entity: LastUsedList<T> = this._resource.value()[key] ?? { key, value: [] };
    const timestamp = this.dateService.now();
    let value: LastUsed<T>[] = [...entity.value];
    const index = value.findIndex((o) => o.id === id);
    const lastUsed = { id, object, timestamp };
    if (index !== -1) value.splice(index, 1);
    value = [lastUsed, ...value];

    if (value.length > 10) value.pop();

    if (hasValue) {
      await this.repository.updateItem(key, { key, value }, { userId });
    } else {
      await this.repository.saveItem(key, { key, value }, { userId });
    }

    this._resource.set({
      ...this._resource.value(),
      [key]: { key, value },
    });

    return lastUsed;
  }

  async clear<T>(key: string): Promise<LastUsed<T>[]> {
    const userId = this.authService.userId();
    if(!userId)
      throw new Error('[LastUsedService] clear: userId is not defined');
    const result = await this.repository.deleteItem(key, { userId });
    return result.value;
  }
}
